import { wallTimeToUtc } from '../zonedClock';
import { cellText, isBlankCell } from './columns';

/**
 * A cell as ingest takes it for an element (KPR-1899).
 *
 * The server is the judge of a value, and a value it can already read goes
 * through as the cell says: a number, a phone, a measurement, a URL, a yes or
 * no. So this module turns only what ingest refuses on purpose but a person's
 * spreadsheet is full of. Ingest is a machine door and asks for one spelling:
 * `2026-03-14`, a choice's stored value. A person's file says `3/14/2026` and
 * the label they read. What this cannot read it hands over unchanged, and the
 * dry run reports it on the row.
 *
 * Text stays text. A cell holding `=HYPERLINK(...)` is that string, in a text
 * element and everywhere else; keepr never evaluates a stored value.
 */

export const DATE_TYPES = new Set(['date', 'date-time']);
const pad = n => String(n).padStart(2, '0');

const MONTHS = ['jan', 'feb', 'mar', 'apr', 'may', 'jun', 'jul', 'aug', 'sep', 'oct', 'nov', 'dec'];
const monthOf = word => MONTHS.indexOf(String(word).slice(0, 3).toLowerCase());

const realDay = (y, m, d) => {
	const moment = new Date(Date.UTC(y, m, d));
	return moment.getUTCFullYear() === y && moment.getUTCMonth() === m && moment.getUTCDate() === d;
};

const iso = (y, m, d) => (realDay(y, m, d) ? `${y}-${pad(m + 1)}-${pad(d)}` : null);

// A spreadsheet's own pivot: 00 to 29 are this century, 30 to 99 the last.
const fullYear = text => {
	const n = Number(text);
	if (text.length > 2) return n;
	return n < 30 ? 2000 + n : 1900 + n;
};

const NUMERIC_DATE = /^(\d{1,4})[/.-](\d{1,2})[/.-](\d{1,4})$/;
const NAMED_FIRST = /^([A-Za-z]{3,9})\.?\s+(\d{1,2})(?:st|nd|rd|th)?,?\s+(\d{4})$/;
const DAY_FIRST = /^(\d{1,2})(?:st|nd|rd|th)?[\s-]([A-Za-z]{3,9})\.?,?[\s-](\d{2,4})$/;

/** The date part of a date text as parts, or null. `order` settles 3/4/2026. */
function readDateText(text, order) {
	const value = text.trim();
	let m = value.match(NUMERIC_DATE);
	if (m) {
		if (m[1].length === 4) return iso(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
		if (order === 'ymd') return m[1].length === 2 ? iso(fullYear(m[1]), Number(m[2]) - 1, Number(m[3])) : null;
		if (m[3].length === 3 || m[3].length === 1) return null;
		const [month, day] = order === 'dmy' ? [m[2], m[1]] : [m[1], m[2]];
		return iso(fullYear(m[3]), Number(month) - 1, Number(day));
	}
	m = value.match(NAMED_FIRST);
	if (m && monthOf(m[1]) >= 0) return iso(Number(m[3]), monthOf(m[1]), Number(m[2]));
	m = value.match(DAY_FIRST);
	if (m && monthOf(m[2]) >= 0) return iso(fullYear(m[3]), monthOf(m[2]), Number(m[1]));
	return null;
}

const TIME_AT_END = /[T\s]+(\d{1,2})(?::(\d{2})(?::(\d{2}))?(?:\.\d+)?)?\s*([ap]\.?m\.?)?$/i;

const clockOf = (hours, minutes, seconds, meridiem) => {
	let hh = Number(hours);
	const mm = Number(minutes || 0);
	const ss = Number(seconds || 0);
	if (meridiem) {
		if (hh < 1 || hh > 12) return null;
		const pm = /^p/i.test(meridiem);
		hh = (hh % 12) + (pm ? 12 : 0);
	}
	if (hh > 23 || mm > 59 || ss > 59) return null;
	return { hh, mm, ss };
};

const clockText = ({ hh, mm, ss }) => `${pad(hh)}:${pad(mm)}${ss ? `:${pad(ss)}` : ''}`;

/**
 * A text cut into its date and the clock after it. A bare number at the end
 * is not a clock ("14 Mar 26" ends in a year): a clock has minutes or an am/pm.
 */
function splitClock(text) {
	const at = text.match(TIME_AT_END);
	if (!at || (at[2] === undefined && !at[4])) return { datePart: text, clock: null, unreadable: false };
	const clock = clockOf(at[1], at[2], at[3], at[4]);
	return { datePart: text.slice(0, at.index), clock, unreadable: !clock };
}

/**
 * What a column's date texts say about their order.
 *   { order: 'dmy' | 'mdy', settled: true }   a value proves it: 13/2/2026
 *   { order: null, settled: false }           3/4/2026 could be either: ask
 *   { order: null, settled: true }            nothing to settle
 * A column that proves both ways holds both, and goes as the person says.
 */
export function readDateOrder(cells) {
	let dayFirst = false;
	let monthFirst = false;
	let ambiguous = false;
	const judge = text => {
		const m = text.trim().split(/[\sT]/)[0].match(NUMERIC_DATE);
		if (!m || m[1].length === 4) return;
		const first = Number(m[1]);
		const second = Number(m[2]);
		// 26/03/14 may lead with its year, and 13/45/1950 is no date either way
		// round, so neither proves an order.
		if (first > 12 && m[3].length === 2) ambiguous = true;
		else if (first > 12 && first <= 31 && second <= 12) dayFirst = true;
		else if (second > 12 && second <= 31 && first <= 12) monthFirst = true;
		else if (first <= 12 && second <= 12 && first !== second) ambiguous = true;
	};
	cells.forEach(cell => {
		if (typeof cell === 'string') cell.split(';').forEach(judge);
	});
	if (dayFirst && !monthFirst) return { order: 'dmy', settled: true };
	if (monthFirst && !dayFirst) return { order: 'mdy', settled: true };
	return { order: null, settled: !dayFirst && !monthFirst && !ambiguous };
}

/** The order a person's own date format reads: a pattern that starts with D reads day first. */
export const preferredDateOrder = pattern => (/^\s*D/i.test(String(pattern || '')) ? 'dmy' : 'mdy');

/** A date element's value, or the text unchanged for the dry run to refuse. */
function dateValue(cell, order) {
	if (cell?.kind === 'date' && cell.date) return cell.date;
	const text = cellText(cell).trim();
	// A month or a year alone is the precision element's own spelling; a full
	// ISO date and a timestamp are ingest's.
	if (/^\d{4}(-\d{2})?(-\d{2})?$/.test(text) || /^\d{4}-\d{2}-\d{2}T/.test(text)) return text;
	return readDateText(splitClock(text).datePart, order) || text;
}

/**
 * A date-time element holds an instant, and a spreadsheet holds a wall clock
 * with no zone: read in the collection's zone, as an import is dated. Ingest
 * reads a bare date as midnight UTC, the evening before in Oregon, so a bare
 * date is turned here too.
 */
function dateTimeValue(cell, order, zone) {
	let date = null;
	let clock = null;
	if (cell?.kind === 'date') {
		date = cell.date;
		if (cell.time) {
			const [hh, mm, ss] = cell.time.split(':').map(Number);
			clock = { hh, mm, ss };
		}
	} else {
		const text = cellText(cell).trim();
		// A timestamp that names its zone already says what it means.
		if (/^\d{4}-\d{2}-\d{2}T.*(?:Z|[+-]\d{2}:?\d{2})$/i.test(text)) return text;
		const split = splitClock(text);
		clock = split.clock;
		date = readDateText(split.datePart, order);
		// A clock that is no clock is the dry run's to refuse, never midnight.
		if (!date || split.unreadable) return text;
	}
	if (!date || !zone) return date || cellText(cell).trim();
	const [y, m, d] = date.split('-').map(Number);
	const { hh = 0, mm = 0, ss = 0 } = clock || {};
	return new Date(wallTimeToUtc(zone, y, m - 1, d, hh, mm).getTime() + ss * 1000).toISOString();
}

function timeValue(cell) {
	if (cell?.kind === 'date' && cell.time) return cell.time.endsWith(':00') ? cell.time.slice(0, 5) : cell.time;
	const text = cellText(cell).trim();
	const m = text.match(/^(\d{1,2})(?::(\d{2}))?(?::(\d{2}))?\s*([ap]\.?m\.?)?$/i);
	if (!m || (!m[2] && !m[4])) return text;
	const clock = clockOf(m[1], m[2], m[3], m[4]);
	return clock ? clockText(clock) : text;
}

const foldChoice = text => String(text).trim().toLowerCase();

/** One choice as stored: an exact value, else the label a person reads, in any case. */
function choiceOf(element, text) {
	const choices = Array.isArray(element.choices) ? element.choices : [];
	const value = text.trim();
	const exact = choices.find(choice => String(choice?.value) === value);
	if (exact) return String(exact.value);
	const folded = foldChoice(value);
	const named = choices.find(choice => foldChoice(choice?.label ?? '') === folded || foldChoice(choice?.value ?? '') === folded);
	return named ? String(named.value) : value;
}

/** The pieces of a cell that holds several values: `;`, and `,` as keepr's own exports write some. */
export function splitValues(text) {
	const value = String(text);
	const pieces = value.includes(';') ? value.split(';') : value.split(',');
	return pieces.map(piece => piece.trim()).filter(Boolean);
}

function choiceValue(element, cell) {
	const text = cellText(cell);
	if (!element.allowMultiple) return choiceOf(element, text);
	const whole = choiceOf(element, text);
	if ((element.choices || []).some(choice => String(choice?.value) === whole)) return [whole];
	return splitValues(text).map(piece => choiceOf(element, piece));
}

// 1.250,50 in a file that writes its decimals with a comma. Ingest reads en-US,
// where 1,250 is a thousand and more, so this turns it before ingest reads it.
const COMMA_DECIMAL = /^-?(\d{1,3}(\.\d{3})+|\d+),\d+$/;
const numberText = (text, decimalComma) => (decimalComma && COMMA_DECIMAL.test(text) ? text.replace(/\./g, '').replace(',', '.') : text);

function numberValue(element, cell, decimalComma) {
	if (cell?.kind !== 'number') return numberText(cellText(cell).trim(), decimalComma);
	// A percent cell holds 0.125 and reads 12.5%; a percent element stores what it reads.
	return element.percent ? cell.text : cell.value;
}

// Only digits and how they are written: a cell that says its own unit keeps it.
const BARE_AMOUNT = /^-?[\d.,/ ]*\d[\d.,/ ]*$/;

function amountValue(element, cell, suffix, decimalComma) {
	if (cell?.kind !== 'number') {
		const text = numberText(cellText(cell).trim(), decimalComma);
		return suffix && BARE_AMOUNT.test(text) ? `${text} ${suffix}` : text;
	}
	const money = element.dataType === 'currency';
	// A worked-out price holds 21.3993 and reads $21.40; an amount is what it reads.
	const value = money && cell.amount !== undefined ? cell.amount : cell.value;
	if (suffix) return `${value} ${suffix}`;
	if (!money || !cell.symbol) return value;
	return /^[A-Z]{3}$/.test(cell.symbol) ? `${value} ${cell.symbol}` : `${cell.symbol}${value}`;
}

/** A yes or no: a real boolean, the captions this element shows, else the words ingest reads. */
function booleanValue(element, cell) {
	if (cell?.kind === 'boolean') return cell.value;
	const text = cellText(cell).trim();
	if (element.trueLabel && foldChoice(element.trueLabel) === foldChoice(text)) return true;
	if (element.falseLabel && foldChoice(element.falseLabel) === foldChoice(text)) return false;
	return text;
}

/**
 * -> what to send, or undefined for a blank (left out, never ''). A lookup and
 * a person are not here: their names are settled together (nameLookups.js).
 *
 * `options`: { order, zone, suffix, decimalComma }, the date order for this
 * column, the collection's time zone, the unit or currency its name carries,
 * and whether the file writes its decimals with a comma.
 */
export function cellValue(element, cell, { order = null, zone = null, suffix = null, decimalComma = false } = {}) {
	if (isBlankCell(cell)) return undefined;
	const type = element.dataType;
	if (type === 'date' || type === 'date-time') {
		const one = type === 'date' ? piece => dateValue(piece, order) : piece => dateTimeValue(piece, order, zone);
		if (!element.allowMultiple) return one(cell);
		return typeof cell === 'string' ? cell.split(';').map(piece => piece.trim()).filter(Boolean).map(one) : [one(cell)];
	}
	if (type === 'time') return timeValue(cell);
	if (type === 'choice') return choiceValue(element, cell);
	if (type === 'number' || type === 'decimal' || type === 'integer') return numberValue(element, cell, decimalComma);
	if (type === 'currency' || type === 'measurement') return amountValue(element, cell, suffix, decimalComma);
	if (type === 'boolean') return booleanValue(element, cell);
	if (type === 'text-small' || type === 'text-large' || type === 'rich-text') return cellText(cell);
	return cellText(cell).trim();
}
